import React, { useCallback, useEffect, useRef, useState } from "react";
import { prefersReducedMotion } from "../../../styles/particleField";
import "./holoCard.css";

/**
 * Maximum tilt at the very edge of the card, in degrees.
 *
 * This and the glare's opacity in holoCard.css are the two numbers worth
 * turning if the effect ever reads as too much or too little.
 */
const MAX_TILT = 24;
/** How far the card lifts toward the viewer while being explored. */
const ACTIVE_SCALE = 1.03;

/**
 * HoloCard — pointer-driven tilt and glare for a card's art.
 *
 * Wraps arbitrary children (in practice a CardImage) and writes pointer
 * position to CSS custom properties; holoCard.css turns those into a 3D tilt
 * and a highlight that tracks the pointer. Nothing here computes styles in JS —
 * the component's whole job is to publish four numbers per frame, which is what
 * keeps this affordable on a mid-range phone.
 *
 * Writes are coalesced into one rAF callback because a WebView can fire
 * pointermove far faster than it paints, and the surplus events were the
 * difference between a smooth tilt and a stuttering one on Android.
 *
 * `active` is deliberately caller-controlled rather than derived here: the
 * treatment means "this is a chase card", so the decision belongs with whatever
 * knows the card's rarity. A card that is not foil in real life must not catch
 * the light here.
 */
const HoloCard = ({
  active = true,
  radius = 10,
  className = "",
  style,
  children,
  ...rest
}) => {
  const rootRef = useRef(null);
  const frameRef = useRef(0);
  const pendingRef = useRef(null);
  const [engaged, setEngaged] = useState(false);
  const reduced = prefersReducedMotion();
  const enabled = active && !reduced;

  const paint = useCallback(() => {
    frameRef.current = 0;
    const node = rootRef.current;
    const next = pendingRef.current;
    if (!node || !next) return;

    node.style.setProperty("--holo-x", `${next.x}%`);
    node.style.setProperty("--holo-y", `${next.y}%`);
    node.style.setProperty("--holo-rx", `${next.rx}deg`);
    node.style.setProperty("--holo-ry", `${next.ry}deg`);
  }, []);

  const schedule = useCallback(
    (payload) => {
      pendingRef.current = payload;
      if (frameRef.current) return;
      frameRef.current = requestAnimationFrame(paint);
    },
    [paint]
  );

  const track = useCallback(
    (clientX, clientY) => {
      const node = rootRef.current;
      if (!node) return;
      const rect = node.getBoundingClientRect();
      if (!rect.width || !rect.height) return;

      // Clamped because a pointer that leaves during a drag would otherwise
      // send the tilt past the card's own edges and the effect breaks apart.
      const x = Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100));
      const y = Math.min(100, Math.max(0, ((clientY - rect.top) / rect.height) * 100));

      schedule({
        x: x.toFixed(2),
        y: y.toFixed(2),
        // Inverted on X: pushing the pointer toward the top of the card should
        // tip its top edge away, which is what a real card in a hand does.
        rx: (((50 - y) / 50) * MAX_TILT).toFixed(2),
        ry: (((x - 50) / 50) * MAX_TILT).toFixed(2),
      });
    },
    [schedule]
  );

  const engage = useCallback(() => {
    const node = rootRef.current;
    if (!node) return;
    setEngaged(true);
    node.style.setProperty("--holo-intensity", "1");
    node.style.setProperty("--holo-scale", String(ACTIVE_SCALE));
  }, []);

  const release = useCallback(() => {
    const node = rootRef.current;
    setEngaged(false);
    if (!node) return;
    node.style.setProperty("--holo-intensity", "0");
    node.style.setProperty("--holo-scale", "1");
    node.style.setProperty("--holo-rx", "0deg");
    node.style.setProperty("--holo-ry", "0deg");
    // Park the light back at the centre so the next engage starts neutral
    // instead of snapping from wherever the pointer last left it.
    node.style.setProperty("--holo-x", "50%");
    node.style.setProperty("--holo-y", "50%");
  }, []);

  const handlePointerMove = (e) => {
    if (!enabled) return;
    // A touch drag that is really a page scroll should not also tilt the card,
    // but a deliberate press-and-move should. Touch therefore only tracks once
    // the pointer is down; mouse tracks on hover.
    if (e.pointerType !== "mouse" && e.buttons === 0 && !engaged) return;
    if (!engaged) engage();
    track(e.clientX, e.clientY);
  };

  useEffect(
    () => () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    },
    []
  );

  // Turning the effect off mid-life (rarity changes, or the OS motion setting
  // flips) has to clear the properties, or the card freezes mid-tilt.
  useEffect(() => {
    if (!enabled) release();
  }, [enabled, release]);

  if (!active) {
    return (
      <div className={className} style={style} {...rest}>
        {children}
      </div>
    );
  }

  return (
    <div
      ref={rootRef}
      data-testid="holo-card"
      data-holo-active={engaged ? "true" : "false"}
      data-holo-reduced={reduced ? "true" : "false"}
      className={`holo ${className}`.trim()}
      style={{
        "--holo-radius": `${radius}px`,
        "--holo-intensity": 0,
        "--holo-scale": 1,
        ...style,
      }}
      onPointerMove={handlePointerMove}
      onPointerEnter={(e) => {
        if (enabled && e.pointerType === "mouse") engage();
      }}
      onPointerDown={(e) => {
        if (enabled) {
          engage();
          track(e.clientX, e.clientY);
        }
      }}
      onPointerUp={release}
      onPointerLeave={release}
      onPointerCancel={release}
      {...rest}
    >
      <div className="holo__art">{children}</div>
      <div className="holo__layer holo__glare" aria-hidden="true" />
    </div>
  );
};

export default HoloCard;
